import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Camera, RotateCw, RefreshCw, Check, SwitchCamera } from 'lucide-react';

// Camera → capture → crop → hands back a JPEG File via onCapture(file).
// Uses getUserMedia for a live preview. getUserMedia only works on HTTPS/localhost,
// so when it is unavailable we fall back to <input capture>, which opens the
// phone's native camera app; the photo still goes through the crop step.

const MAX_SIDE = 2000;       // downscale large photos before upload
const MIN_CROP = 0.05;       // smallest crop box, as a fraction of the image
const DEFAULT_CROP = { x: 0.05, y: 0.05, w: 0.9, h: 0.9 };

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

export default function CameraCapture({ open, onClose, onCapture }) {
  const [step, setStep] = useState('camera');        // 'camera' | 'crop'
  const [cameraError, setCameraError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [hasMultipleCams, setHasMultipleCams] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState(DEFAULT_CROP);
  const [processing, setProcessing] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const nativeInputRef = useRef(null);
  const cropAreaRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }, []);

  const startStream = useCallback(async (mode) => {
    stopStream();
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('unsupported');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: mode }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      setHasMultipleCams(devices.filter(d => d.kind === 'videoinput').length > 1);
    } catch (err) {
      setCameraError(err?.name === 'NotAllowedError' ? 'denied' : 'unavailable');
    }
  }, [stopStream]);

  // Open / close lifecycle
  useEffect(() => {
    if (open) {
      setStep('camera');
      setImageSrc(null);
      setCrop(DEFAULT_CROP);
      startStream(facingMode);
    } else {
      stopStream();
    }
    return stopStream;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    stopStream();
    onClose();
  };

  const switchCamera = () => {
    const next = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(next);
    startStream(next);
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    setImageSrc(canvas.toDataURL('image/jpeg', 0.95));
    setCrop(DEFAULT_CROP);
    stopStream();
    setStep('crop');
  };

  const handleNativeCapture = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setCrop(DEFAULT_CROP);
      setStep('crop');
    };
    reader.readAsDataURL(file);
  };

  const retake = () => {
    setImageSrc(null);
    setStep('camera');
    if (cameraError) nativeInputRef.current?.click();
    else startStream(facingMode);
  };

  const rotate = () => {
    const img = imgRef.current;
    if (!img) return;
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalHeight;
    canvas.height = img.naturalWidth;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
    setImageSrc(canvas.toDataURL('image/jpeg', 0.95));
    setCrop(DEFAULT_CROP);
  };

  // ---- Crop box dragging (move + corner resize), in fractions of the image ----
  const onPointerDown = (mode) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { mode, startX: e.clientX, startY: e.clientY, start: crop };
  };

  useEffect(() => {
    if (step !== 'crop') return;
    const onMove = (e) => {
      const drag = dragRef.current;
      const area = cropAreaRef.current;
      if (!drag || !area) return;
      const rect = area.getBoundingClientRect();
      const dx = (e.clientX - drag.startX) / rect.width;
      const dy = (e.clientY - drag.startY) / rect.height;
      const s = drag.start;
      let { x, y, w, h } = s;

      if (drag.mode === 'move') {
        x = clamp(s.x + dx, 0, 1 - s.w);
        y = clamp(s.y + dy, 0, 1 - s.h);
      } else {
        if (drag.mode.includes('w')) {
          x = clamp(s.x + dx, 0, s.x + s.w - MIN_CROP);
          w = s.x + s.w - x;
        }
        if (drag.mode.includes('e')) w = clamp(s.w + dx, MIN_CROP, 1 - s.x);
        if (drag.mode.includes('n')) {
          y = clamp(s.y + dy, 0, s.y + s.h - MIN_CROP);
          h = s.y + s.h - y;
        }
        if (drag.mode.includes('s')) h = clamp(s.h + dy, MIN_CROP, 1 - s.y);
      }
      setCrop({ x, y, w, h });
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [step]);

  const confirmCrop = () => {
    const img = imgRef.current;
    if (!img) return;
    setProcessing(true);
    const sx = Math.round(crop.x * img.naturalWidth);
    const sy = Math.round(crop.y * img.naturalHeight);
    const sw = Math.round(crop.w * img.naturalWidth);
    const sh = Math.round(crop.h * img.naturalHeight);
    const scale = Math.min(1, MAX_SIDE / Math.max(sw, sh));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(sw * scale);
    canvas.height = Math.round(sh * scale);
    canvas.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      setProcessing(false);
      if (!blob) return;
      const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
      onCapture(file);
      handleClose();
    }, 'image/jpeg', 0.9);
  };

  if (!open) return null;

  const handle = (pos, cls) => (
    <div
      onPointerDown={onPointerDown(pos)}
      className={`absolute w-6 h-6 -m-3 rounded-full bg-white border-2 border-primary-500 shadow ${cls}`}
      style={{ touchAction: 'none' }}
    />
  );

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 text-white">
        <span className="font-medium">{step === 'camera' ? 'Take a photo' : 'Crop photo'}</span>
        <button type="button" onClick={handleClose} className="p-2 rounded-full hover:bg-white/10" title="Close">
          <X className="w-6 h-6" />
        </button>
      </div>

      <input
        ref={nativeInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleNativeCapture}
        className="hidden"
      />

      {/* Body */}
      <div className="flex-1 min-h-0 flex items-center justify-center px-4">
        {step === 'camera' && !cameraError && (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="max-w-full max-h-full rounded-lg bg-black"
          />
        )}

        {step === 'camera' && cameraError && (
          <div className="text-center text-white max-w-sm space-y-4">
            <p className="text-sm text-gray-300">
              {cameraError === 'denied'
                ? 'Camera permission was denied. Allow camera access in your browser settings, or use your device camera below.'
                : 'Live camera preview is not available here. You can still take a photo with your device camera.'}
            </p>
            <button
              type="button"
              onClick={() => nativeInputRef.current?.click()}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-medium"
            >
              <Camera className="w-5 h-5" /> Open device camera
            </button>
          </div>
        )}

        {step === 'crop' && imageSrc && (
          <div
            ref={cropAreaRef}
            className="relative inline-block overflow-hidden select-none"
            style={{ touchAction: 'none' }}
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Captured"
              draggable={false}
              className="block max-w-full object-contain"
              style={{ maxHeight: 'calc(100vh - 180px)' }}
            />
            <div
              onPointerDown={onPointerDown('move')}
              className="absolute border-2 border-white cursor-move"
              style={{
                left: `${crop.x * 100}%`,
                top: `${crop.y * 100}%`,
                width: `${crop.w * 100}%`,
                height: `${crop.h * 100}%`,
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
                touchAction: 'none',
              }}
            >
              {handle('nw', 'left-0 top-0 cursor-nwse-resize')}
              {handle('ne', 'right-0 top-0 cursor-nesw-resize')}
              {handle('sw', 'left-0 bottom-0 cursor-nesw-resize')}
              {handle('se', 'right-0 bottom-0 cursor-nwse-resize')}
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-6 px-4 py-5" style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom, 0px))' }}>
        {step === 'camera' && !cameraError && (
          <>
            <div className="w-12" />
            <button
              type="button"
              onClick={capture}
              className="w-16 h-16 rounded-full bg-white border-4 border-gray-300 hover:bg-gray-100 active:scale-95 transition"
              title="Capture"
            />
            {hasMultipleCams ? (
              <button type="button" onClick={switchCamera} className="w-12 h-12 flex items-center justify-center rounded-full text-white hover:bg-white/10" title="Switch camera">
                <SwitchCamera className="w-6 h-6" />
              </button>
            ) : <div className="w-12" />}
          </>
        )}

        {step === 'crop' && (
          <>
            <button type="button" onClick={retake} className="flex flex-col items-center text-white text-xs gap-1 hover:opacity-80">
              <RefreshCw className="w-6 h-6" /> Retake
            </button>
            <button type="button" onClick={rotate} className="flex flex-col items-center text-white text-xs gap-1 hover:opacity-80">
              <RotateCw className="w-6 h-6" /> Rotate
            </button>
            <button
              type="button"
              onClick={confirmCrop}
              disabled={processing}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white font-medium"
            >
              <Check className="w-5 h-5" /> Use photo
            </button>
          </>
        )}
      </div>
    </div>
  );
}
